/**
 * Shared pieces for the Record Flow tabs, on Tide: the loader hook, the
 * loading/error rule, an accessible chart frame with a Living Legend beside
 * it, pills, the freshness report for the header band, and date/number
 * formatting.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { Domain, FlowResult, Origin } from './flowApi';
import { DOMAIN_COLOR, DOMAIN_LABEL, DOMAIN_MARKER, ORIGIN_COLOR, ORIGIN_LABEL, ORIGIN_MARKER } from './chartTheme';
import { Marker, STATUS, STATUS_MARKER, StateBlock } from '../../../components/tide';
import type { EmptyReason, SkeletonShape, StatusKey } from '../../../components/tide';

/* ---------- loading ---------- */

export type RemoteState<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string; httpStatus?: number }
  | { status: 'ready'; data: T };

/**
 * Runs `load` whenever `key` changes (the key names every input the request
 * depends on). Old data is dropped while the next request is in flight and a
 * superseded response is ignored, so a slow answer for the previous window can
 * never paint over the current one.
 */
export function useRemote<T>(key: string, load: () => Promise<FlowResult<T>>) {
  const [state, setState] = useState<RemoteState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    loadRef.current().then((r) => {
      if (!live) return;
      setState(r.ok ? { status: 'ready', data: r.data } : { status: 'error', message: r.error, httpStatus: r.status });
    });
    return () => { live = false; };
  }, [key, nonce]);

  return { state, retry: () => setNonce((n) => n + 1) };
}

/* ---------- freshness (for the header band) ---------- */

export interface Freshness { generatedAt: string; timeZone: string }
type ReportFreshness = (f: Freshness | null) => void;
const FreshnessContext = createContext<ReportFreshness>(() => undefined);
/** RecordFlow provides the setter; each tab's loaded payload reports its generatedAt. */
export const FreshnessProvider = FreshnessContext.Provider;

/**
 * Report what the visible tab was generated from. Null while loading, after a
 * failure and on unmount, so the band never presents an old answer as current.
 */
export function useReportFreshness(f: Freshness | null) {
  const report = useContext(FreshnessContext);
  const at = f?.generatedAt ?? null;
  const tz = f?.timeZone ?? null;
  useEffect(() => {
    report(at ? { generatedAt: at, timeZone: tz || 'America/New_York' } : null);
    return () => report(null);
  }, [report, at, tz]);
}

function freshnessOf(state: RemoteState<unknown>): Freshness | null {
  if (state.status !== 'ready') return null;
  const d = state.data as { generatedAt?: unknown; window?: { timeZone?: unknown } } | null;
  if (!d || typeof d.generatedAt !== 'string') return null;
  return { generatedAt: d.generatedAt, timeZone: typeof d.window?.timeZone === 'string' ? d.window.timeZone : 'America/New_York' };
}

/** Loading is a skeleton with a status, failure is an alert with Try again - never an empty view. */
export function Remote<T>({ state, retry, children, skeleton = 'chart', reportFreshness = true }: {
  state: RemoteState<T>; retry: () => void; children: (data: T) => React.ReactNode;
  /** The shape of what is loading, so the layout does not jump when it arrives. */
  skeleton?: SkeletonShape;
  /** False for loads that are not the tab itself (the resident timeline). */
  reportFreshness?: boolean;
}) {
  const report = useContext(FreshnessContext);
  const fresh = reportFreshness ? freshnessOf(state) : null;
  const at = fresh?.generatedAt ?? null;
  const tz = fresh?.timeZone ?? null;
  useEffect(() => {
    if (!reportFreshness) return undefined;
    report(at ? { generatedAt: at, timeZone: tz || 'America/New_York' } : null);
    return () => report(null);
  }, [report, reportFreshness, at, tz]);

  if (state.status === 'loading') return <StateBlock kind="loading" shape={skeleton} />;
  if (state.status === 'error') return <StateBlock kind="error" message={state.message} status={state.httpStatus} onRetry={retry} />;
  return <>{children(state.data)}</>;
}

/* ---------- chart frame ---------- */

/**
 * A titled figure around one chart. The figure carries the accessible name and
 * a visually hidden text summary, so the numbers are available without the
 * picture. The legend sits beside the plot on wide screens and under it on
 * narrow ones; it is outside the hidden picture so its controls stay reachable.
 */
export function ChartCard({
  title, hint, summary, children, className = '', exposeChildren = false, legend, foot, side = false,
}: {
  title: string; hint?: React.ReactNode; summary: string; children: React.ReactNode; className?: string;
  /** Set when the children are real, readable content (a grid) rather than a picture of data. */
  exposeChildren?: boolean;
  /** A Living Legend for this chart. */
  legend?: React.ReactNode;
  /** Caveats under the chart (visible and read). */
  foot?: React.ReactNode;
  /** Put the legend beside the plot from 1024px. */
  side?: boolean;
}) {
  return (
    <figure aria-label={title} className={`tide-chart ${side ? 'tide-chart--side' : ''} ${className}`.trim()}>
      <figcaption>
        <span className="tide-chart__title">{title}</span>
        {hint && <span className="tide-chart__hint">{hint}</span>}
      </figcaption>
      <p className="tide-sr">{summary}</p>
      <div className="tide-chart__body">
        <div className="tide-chart__plot" aria-hidden={exposeChildren ? undefined : true}>{children}</div>
        {legend}
      </div>
      {foot}
    </figure>
  );
}

/** An empty result, in words. `reason` says which kind of empty it is. */
export function EmptyNote({ children, reason = 'none', onClear }: { children: React.ReactNode; reason?: EmptyReason; onClear?: () => void }) {
  return <StateBlock kind="empty" reason={reason} message={children} onClear={onClear} />;
}

/* ---------- pills + marks ---------- */

export function OriginPill({ origin }: { origin: Origin }) {
  return (
    <span className="tide-pill tide-pill--origin" style={{ borderColor: ORIGIN_COLOR[origin] }}>
      <Marker shape={ORIGIN_MARKER[origin]} color={ORIGIN_COLOR[origin]} size={10} />
      {ORIGIN_LABEL[origin]}
    </span>
  );
}

export type PillTone = StatusKey | 'neutral';

/** A status pill: marker + words in the status colours. `neutral` is a plain label. */
export function Pill({ tone, children, title }: { tone: PillTone; children: React.ReactNode; title?: string }) {
  if (tone === 'neutral') {
    return <span title={title} className="tide-pill" style={{ color: 'var(--tide-ink-2)', background: 'var(--tide-surface-2)', borderColor: 'var(--tide-line)' }}>{children}</span>;
  }
  const s = STATUS[tone];
  return (
    <span title={title} className="tide-pill" style={{ color: s.fg, background: s.bg, borderColor: s.fg }}>
      <Marker shape={STATUS_MARKER[tone]} color={s.fg} size={9} />
      {children}
    </span>
  );
}

/** A domain's colour and shape. */
export function DomainMark({ domain, size = 10 }: { domain: Domain; size?: number }) {
  return <Marker shape={DOMAIN_MARKER[domain]} color={DOMAIN_COLOR[domain]} size={size} />;
}

/** A domain's mark beside its name. */
export function DomainName({ domain }: { domain: Domain }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><DomainMark domain={domain} />{DOMAIN_LABEL[domain]}</span>;
}

/* ---------- formatting ---------- */

export const NOT_RECORDED = 'not recorded';

export const fmtN = (n: number | null | undefined) => (n == null ? '-' : n.toLocaleString('en-US'));

export function fmtHours(h: number | null | undefined): string {
  if (h == null) return '-';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}

export const fmtWhen = (iso: string | null | undefined) =>
  (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-');

/** generatedAt in the window's zone, with the zone named: "Oct 1, 8:00 AM EDT". */
export function fmtGenerated(iso: string, timeZone = 'America/New_York'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

/** 'YYYY-MM-DD' to 'Sep 1, 2026' without going through a time zone. */
export function fmtIsoDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}

export const DATE_UNKNOWN = 'Date unknown (imported)';

/**
 * When a step happened. An undated step must not show its import instant as
 * if it were the event time, and a date-only step is a calendar date: it is
 * formatted from the UTC date part, because converting its midnight UTC to New
 * York would land on the previous evening.
 */
export function fmtStepWhen(s: { at: string; timeKnown?: boolean; dateOnly?: boolean }): string {
  if (s.timeKnown === false) return DATE_UNKNOWN;
  if (s.dateOnly) return new Date(s.at).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  return fmtWhen(s.at);
}

/** 'YYYY-MM-DD' to 'M/D' without going through a time zone. */
export const fmtDay = (date: string) => {
  const [, m, d] = date.split('-');
  return `${Number(m)}/${Number(d)}`;
};

/** Wall-clock date in New York, the zone the contract's windows are expressed in. */
export function nyToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

const toUtc = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };

export function addDays(iso: string, n: number): string {
  return new Date(toUtc(iso) + n * 86400000).toISOString().slice(0, 10);
}

/** Inclusive count of days from `from` to `to`. */
export const spanDays = (from: string, to: string) => Math.round((toUtc(to) - toUtc(from)) / 86400000) + 1;

export function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  const n = spanDays(from, to);
  for (let i = 0; i < n && i < 400; i += 1) out.push(addDays(from, i));
  return out;
}

export const stateLabel = (s: string | null) => (s == null ? NOT_RECORDED : s.replace(/_/g, ' ').toLowerCase());
